import { Tile, type MapData } from "@dogdell/shared";

/** Shared tile → RGB for minimap / travel map. */
export function tileColor(tile: Tile): [number, number, number] {
  switch (tile) {
    case Tile.Grass:
      return [168, 212, 100];
    case Tile.GrassAlt:
      return [158, 204, 92];
    case Tile.Path:
      return [196, 187, 176];
    case Tile.Pier:
      return [160, 118, 72];
    case Tile.Sand:
      return [232, 200, 130];
    case Tile.Floor:
      return [210, 168, 110];
    case Tile.CourtBasket:
      return [92, 98, 106];
    case Tile.CourtTennis:
      return [61, 138, 90];
    case Tile.CourtLine:
      return [244, 242, 234];
    case Tile.Water:
      return [78, 168, 188];
    case Tile.Tree:
      return [58, 118, 54];
    case Tile.Bush:
      return [72, 136, 64];
    case Tile.Flowers:
      return [156, 200, 104];
    case Tile.Rock:
      return [130, 120, 110];
    case Tile.Fence:
      return [110, 80, 50];
    default:
      return [150, 196, 88];
  }
}

/** Soft illustrator fills for the Travel park map. */
function stylizedFill(tile: Tile): string {
  switch (tile) {
    case Tile.Grass:
      return "#a8d464";
    case Tile.GrassAlt:
      return "#9cc85c";
    case Tile.Path:
      return "#c4bbb0";
    case Tile.Pier:
      return "#c49a6c";
    case Tile.Sand:
      return "#e8c878";
    case Tile.Floor:
      return "#d4b488";
    case Tile.CourtBasket:
      return "#5c6168";
    case Tile.CourtTennis:
      return "#3d8a5a";
    case Tile.CourtLine:
      return "#f4f2ea";
    case Tile.Water:
      return "#4ea8bc";
    case Tile.Tree:
      return "#3d7a38";
    case Tile.Bush:
      return "#4a8a42";
    case Tile.Flowers:
      return "#8ec050";
    case Tile.Rock:
      return "#9a9084";
    case Tile.Fence:
      return "#7a5a38";
    default:
      return "#8ec848";
  }
}

function hexRgb(hex: string): [number, number, number] {
  return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
}

/** 1px-per-tile terrain bake of the live zone map. */
export function bakeMapTerrain(map: MapData): HTMLCanvasElement {
  const bake = document.createElement("canvas");
  bake.width = map.width;
  bake.height = map.height;
  const g = bake.getContext("2d")!;
  const img = g.createImageData(map.width, map.height);
  const data = img.data;
  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) {
      const tile = map.tiles[y]?.[x] ?? Tile.Grass;
      const rgb = tileColor(tile);
      const i = (y * map.width + x) * 4;
      data[i] = rgb[0];
      data[i + 1] = rgb[1];
      data[i + 2] = rgb[2];
      data[i + 3] = 255;
    }
  }
  g.putImageData(img, 0, 0);
  return bake;
}

/**
 * Illustrated park poster for Travel: soft lawn wash, ribbon sidewalks,
 * canopy blobs, and pond sheen - not a raw pixel dump of every tile.
 */
export function bakeStylizedTravelMap(map: MapData, outSize = 720): HTMLCanvasElement {
  const w = map.width;
  const h = map.height;
  const out = document.createElement("canvas");
  out.width = outSize;
  out.height = outSize;
  const g = out.getContext("2d")!;
  const sx = outSize / w;
  const sy = outSize / h;

  const lawn = g.createLinearGradient(0, 0, outSize, outSize);
  lawn.addColorStop(0, "#9ed050");
  lawn.addColorStop(0.45, "#7eb848");
  lawn.addColorStop(1, "#6aa838");
  g.fillStyle = lawn;
  g.fillRect(0, 0, outSize, outSize);

  // Low-res category pass → smooth upscale (painterly base)
  const low = document.createElement("canvas");
  low.width = w;
  low.height = h;
  const lg = low.getContext("2d")!;
  const img = lg.createImageData(w, h);
  const data = img.data;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const tile = map.tiles[y]?.[x] ?? Tile.Grass;
      let paint = tile;
      if (tile === Tile.Bush || tile === Tile.Tree) paint = Tile.Tree;
      const [r, gr, b] = hexRgb(stylizedFill(paint));
      const i = (y * w + x) * 4;
      data[i] = r;
      data[i + 1] = gr;
      data[i + 2] = b;
      data[i + 3] = 255;
    }
  }
  lg.putImageData(img, 0, 0);
  g.imageSmoothingEnabled = true;
  g.imageSmoothingQuality = "high";
  g.globalAlpha = 0.92;
  g.drawImage(low, 0, 0, outSize, outSize);
  g.globalAlpha = 1;

  // Sidewalks: prefer curved ribbons when present (matches in-world paths).
  g.lineCap = "round";
  g.lineJoin = "round";
  const pathW = Math.max(sx, sy) * 1.65;
  const ribbons = map.pathRibbons ?? [];
  if (ribbons.length > 0) {
    for (const ribbon of ribbons) {
      if (ribbon.points.length < 2) continue;
      const half = Math.max(0.55, ribbon.width * 0.5) * Math.min(sx, sy);
      g.strokeStyle = "rgba(180, 174, 160, 0.95)";
      g.lineWidth = half * 2 + Math.max(sx, sy) * 0.35;
      strokeRibbon(g, ribbon.points, sx, sy);
      g.strokeStyle = "rgba(154, 148, 136, 0.96)";
      g.lineWidth = half * 2;
      strokeRibbon(g, ribbon.points, sx, sy);
    }
  } else {
    g.strokeStyle = "rgba(180, 174, 160, 0.95)";
    g.lineWidth = pathW + Math.max(sx, sy) * 0.45;
    strokePathGraph(g, map, sx, sy);
    g.strokeStyle = "rgba(154, 148, 136, 0.96)";
    g.lineWidth = pathW;
    strokePathGraph(g, map, sx, sy);
  }

  // Water sheen on pond tiles
  g.fillStyle = "rgba(90, 190, 220, 0.32)";
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if ((map.tiles[y]?.[x] ?? Tile.Grass) !== Tile.Water) continue;
      const n =
        Number((map.tiles[y - 1]?.[x] ?? Tile.Grass) === Tile.Water) +
        Number((map.tiles[y + 1]?.[x] ?? Tile.Grass) === Tile.Water) +
        Number((map.tiles[y]?.[x - 1] ?? Tile.Grass) === Tile.Water) +
        Number((map.tiles[y]?.[x + 1] ?? Tile.Grass) === Tile.Water);
      if (n < 3) continue;
      g.beginPath();
      g.ellipse((x + 0.5) * sx, (y + 0.5) * sy, sx * 0.85, sy * 0.55, 0, 0, Math.PI * 2);
      g.fill();
    }
  }

  // Tree canopy blobs
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const tile = map.tiles[y]?.[x] ?? Tile.Grass;
      if (tile !== Tile.Tree && tile !== Tile.Bush) continue;
      const neighbors =
        Number((map.tiles[y]?.[x + 1] ?? Tile.Grass) === tile) +
        Number((map.tiles[y + 1]?.[x] ?? Tile.Grass) === tile);
      if (neighbors === 0 && (x + y) % 3 !== 0) continue;
      g.fillStyle = tile === Tile.Tree ? "rgba(40, 100, 48, 0.55)" : "rgba(55, 120, 58, 0.4)";
      g.beginPath();
      g.arc((x + 0.5) * sx, (y + 0.5) * sy, Math.max(sx, sy) * (tile === Tile.Tree ? 0.95 : 0.7), 0, Math.PI * 2);
      g.fill();
    }
  }

  // Flower accents
  g.fillStyle = "rgba(232, 140, 180, 0.55)";
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if ((map.tiles[y]?.[x] ?? Tile.Grass) !== Tile.Flowers) continue;
      if ((x * 17 + y * 13) % 5 !== 0) continue;
      g.beginPath();
      g.arc((x + 0.35) * sx, (y + 0.4) * sy, Math.max(1.2, sx * 0.22), 0, Math.PI * 2);
      g.fill();
    }
  }

  const vig = g.createRadialGradient(
    outSize * 0.5,
    outSize * 0.5,
    outSize * 0.28,
    outSize * 0.5,
    outSize * 0.5,
    outSize * 0.72,
  );
  vig.addColorStop(0, "rgba(255, 248, 220, 0)");
  vig.addColorStop(1, "rgba(120, 72, 36, 0.2)");
  g.fillStyle = vig;
  g.fillRect(0, 0, outSize, outSize);

  g.strokeStyle = "rgba(110, 72, 36, 0.4)";
  g.lineWidth = Math.max(3, outSize * 0.008);
  g.strokeRect(outSize * 0.012, outSize * 0.012, outSize * 0.976, outSize * 0.976);

  return out;
}

function strokeRibbon(
  g: CanvasRenderingContext2D,
  points: readonly { x: number; y: number }[],
  sx: number,
  sy: number,
): void {
  g.beginPath();
  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    const px = p.x * sx;
    const py = p.y * sy;
    if (i === 0) g.moveTo(px, py);
    else g.lineTo(px, py);
  }
  g.stroke();
}

function strokePathGraph(
  g: CanvasRenderingContext2D,
  map: MapData,
  sx: number,
  sy: number,
): void {
  const w = map.width;
  const h = map.height;
  g.beginPath();
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if ((map.tiles[y]?.[x] ?? Tile.Grass) !== Tile.Path) continue;
      const right = (map.tiles[y]?.[x + 1] ?? Tile.Grass) === Tile.Path;
      const down = (map.tiles[y + 1]?.[x] ?? Tile.Grass) === Tile.Path;
      const cx = (x + 0.5) * sx;
      const cy = (y + 0.5) * sy;
      if (right) {
        g.moveTo(cx, cy);
        g.lineTo((x + 1.5) * sx, cy);
      }
      if (down) {
        g.moveTo(cx, cy);
        g.lineTo(cx, (y + 1.5) * sy);
      }
      if (!right && !down) {
        g.moveTo(cx, cy);
        g.lineTo(cx + 0.01, cy);
      }
    }
  }
  g.stroke();
}
